import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { SITE, IMG } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal } from '../components/motion';
import { PageHero } from '../components/chrome';
import { RFQForm } from '../components/RFQForm';

const crumbs = [{ label: 'Home', href: '/' }, { label: 'Contact / Request a Quote' }];

const STEPS = [
  ['01', 'Submit', 'Send the requirement below — equipment, service, quantity, and deadline if known. A file with specs or a SOW helps.'],
  ['02', 'Respond', 'You receive a formatted, procurement-ready quote or a short list of clarifying questions, typically within one business day.'],
  ['03', 'Execute', 'Approved orders move to delivery, installation, and acceptance documentation — with lifecycle support after.'],
];

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact & Request a Quote | ToxGlobal Analytics"
        description="Request a quote or send an RFQ to ToxGlobal Analytics. Government purchase cards, POs, and ACH accepted. Call 877.225.0316 or email info@toxglobal.com — nationwide analytical equipment and support."
        path="/contact"
        breadcrumbs={crumbs}
      />
      <PageHero
        eyebrow="Contact / RFQ"
        lines="Send the requirement. We'll handle the rest."
        sub="Equipment, service, or a solicitation on the street — this form reaches the team that quotes and coordinates it."
        breadcrumbs={crumbs}
        image={IMG.installTech}
        imageAlt="ToxGlobal Analytics equipment installation coordination"
      />

      <section className="section-pad" data-testid="contact-body">
        <div className="container-x grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start">
          <div className="space-y-6">
            <Reveal>
              <div className="bg-ink text-white p-8" data-testid="contact-card">
                <h2 className="display text-2xl">Direct lines</h2>
                <div className="mt-6 space-y-4 text-sm">
                  <a href={SITE.phoneHref} className="flex items-center gap-3 text-slate-200 hover:text-signal transition-colors" data-testid="contact-phone-link">
                    <Phone size={16} className="text-signal" /> <span className="font-mono tracking-wide">{SITE.phone}</span>
                  </a>
                  <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-slate-200 hover:text-signal transition-colors" data-testid="contact-email-link">
                    <Mail size={16} className="text-signal" /> {SITE.email}
                  </a>
                  <p className="flex items-center gap-3 text-slate-400"><MapPin size={16} className="text-signal" /> Serving all 50 states</p>
                  <p className="flex items-center gap-3 text-slate-400"><Clock size={16} className="text-signal" /> Mon–Fri, 8:00–18:00 ET</p>
                </div>
                <div className="mt-7 pt-6 border-t border-white/10 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400 space-y-1">
                  <p>UEI {SITE.uei} · CAGE {SITE.cage}</p>
                  <p>SAM.gov Active · GPC / PO / ACH accepted</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.07}>
              <div className="border border-slate-300 bg-white p-8" data-testid="contact-process">
                <h3 className="eyebrow text-signal mb-5">What happens next</h3>
                <ol className="space-y-5">
                  {STEPS.map(([n, t, d]) => (
                    <li key={n} className="flex gap-4">
                      <span className="font-mono text-xs text-signal tabular mt-1">{n}</span>
                      <div>
                        <p className="font-display font-semibold text-ink">{t}</p>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.05}><RFQForm /></Reveal>
        </div>
      </section>
    </div>
  );
}
